import { ComboboxSelect } from '@chronosphereio/chrono-ui';
import { Group } from '@/dashboards/components/Builder';
import { useLabelValues } from '../promql-builder-apis';
import { useBuilderContext, useLabelMatchers } from '../promql-builder-context';
import { Filter, hasRegexOperator } from '../promql-builder-model';

type FilterValueSelectorProps = {
  filter: Filter;
  onFilterChange: (filter: Filter) => void;
  isLast?: boolean;
};

export function FilterValueSelector({ filter, onFilterChange, isLast }: FilterValueSelectorProps) {
  const { metric } = useBuilderContext();

  // Options
  const matchers = useLabelMatchers(filter.label);
  const { data: options, isLoading } = useLabelValues(filter.label, metric, matchers);

  // Value
  const values = filter.values ?? null;

  const hasLabel = filter.label !== '';
  const hasValues = filter.values.length > 0;
  const isEmptyString = filter.values.length === 1 && filter.values[0] === '';
  const valuesWithTranslatedEmptyString = filter.values.map((v) => (v === '' ? '(empty string)' : v));

  // Switch between isMulti={true} and isMulti={false} depending on whether we have regex operators (i.e. =~ or !~)
  return hasRegexOperator(filter) ? (
    <ComboboxSelect
      isMulti={true}
      isLoading={hasLabel && isLoading}
      showFooter={hasLabel}
      value={values}
      renderValue={
        <Group.Button isLast={isLast} isPlaceholder={!hasValues || isEmptyString}>
          {hasValues ? valuesWithTranslatedEmptyString.join(' | ') : 'Value'}
        </Group.Button>
      }
      searchPlaceholder={'Search or enter a custom regex (ex. prod.*)'}
      options={options}
      noOptionsText={!hasLabel ? 'Please select a label first' : undefined}
      onSelect={(options) => {
        if (!options) {
          onFilterChange({ ...filter, values: [] });
        } else {
          onFilterChange({ ...filter, values: options });
        }
      }}
    />
  ) : (
    <ComboboxSelect
      isMulti={false}
      isLoading={hasLabel && isLoading}
      showFooter={hasLabel}
      value={values[0] ?? null}
      renderValue={
        <Group.Button isLast={isLast} isPlaceholder={!hasValues || isEmptyString}>
          {hasValues ? valuesWithTranslatedEmptyString[0] : 'Value'}
        </Group.Button>
      }
      searchPlaceholder={'Search values'}
      options={options}
      noOptionsText={!hasLabel ? 'Please select a label first' : undefined}
      onSelect={(option) => {
        if (!option) {
          onFilterChange({ ...filter, values: [] });
        } else {
          onFilterChange({ ...filter, values: [option] });
        }
      }}
    />
  );
}
